import { RootState } from "app/store";
import {
  Roster,
  isPlaceholderRoster,
  getRosterId,
  isPlaceholderRosterId,
} from "model/Roster/Roster";
import { selectTournamentTeams } from "./Tournament";
import {
  getTournamentDisplayTrigram,
  getTournamentDriverName,
  getTournamentDriverDetails,
  getTournamentRosterIds,
  getTournamentRosterIdFromDriverId,
} from "model/Tournament/TournamentTeams";
import { compareAlphabetically } from "Utility";

export const selectTournamentRosterIds = (
  state: RootState,
  tournamentId: string
) => {
  const teams = selectTournamentTeams(state, tournamentId);
  return !!teams ? getTournamentRosterIds(teams) : undefined;
};

export const selectTournamentRoster = (
  state: RootState,
  rosterId: string,
  tournamentId: string
) =>
  selectTournamentTeams(state, tournamentId)?.rosters.find(
    (r) => r.id === rosterId
  );

export const selectTournamentRosterIdForDriverId = (
  state: RootState,
  driverId: string,
  tournamentId: string
) => {
  const teams = selectTournamentTeams(state, tournamentId);
  return !!teams
    ? getTournamentRosterIdFromDriverId(teams, driverId)
    : undefined;
};

export const selectRosterScope = (state: RootState, tournamentId: string) =>
  selectTournamentTeams(state, tournamentId)?.scope;

export const selectRosterDisplayName = (
  state: RootState,
  rosterId: string,
  tournamentId: string
) => {
  if (isPlaceholderRosterId(rosterId)) {
    return rosterId;
  }

  return selectTournamentRoster(state, rosterId, tournamentId)?.name;
};

export const selectRosterDisplayNameOrPlaceholder = (
  state: RootState,
  roster: Roster,
  tournamentId: string
) => {
  if (isPlaceholderRoster(roster)) {
    return roster;
  }

  const rosterId = getRosterId(roster);
  return !!rosterId
    ? selectRosterDisplayName(state, rosterId, tournamentId)
    : undefined;
};

export const selectRosterDisplayTrigram = (
  state: RootState,
  rosterId: string,
  tournamentId: string
) => {
  if (isPlaceholderRoster(rosterId)) {
    return rosterId;
  }

  const teams = selectTournamentTeams(state, tournamentId);
  return !!teams ? getTournamentDisplayTrigram(teams, rosterId) : undefined;
};

export const selectRosterIdsAlphabetically = (
  state: RootState,
  tournamentId: string,
  rosterIds: string[]
) => {
  const sortedRosterIds = rosterIds
    .map((id) => ({
      id,
      displayName: selectRosterDisplayName(state, id, tournamentId),
    }))
    .sort((a, b) => compareAlphabetically(a.displayName, b.displayName));
  return sortedRosterIds.map(({ id }) => id);
};

export const selectRosterOrgId = (
  state: RootState,
  rosterId: string,
  tournamentId: string
) => {
  if (isPlaceholderRosterId(rosterId)) {
    return undefined;
  }

  return selectTournamentRoster(state, rosterId, tournamentId)?.orgId;
};

export const selectRosterOrgDetails = (
  state: RootState,
  rosterId: string,
  tournamentId: string
) => {
  if (isPlaceholderRosterId(rosterId)) {
    return undefined;
  }

  const roster = selectTournamentRoster(state, rosterId, tournamentId);
  return !!roster ? { id: roster.orgId, name: roster.orgName } : undefined;
};

export const selectDriverOrgDetails = (
  state: RootState,
  driverId: string,
  tournamentId: string
) => {
  const rosterId = selectTournamentRosterIdForDriverId(
    state,
    driverId,
    tournamentId
  );
  return !!rosterId
    ? selectRosterOrgDetails(state, rosterId, tournamentId)
    : undefined;
};

export const selectDriverOrgDetailsFromTournaments = (
  state: RootState,
  driverId: string,
  tournamentIds: string[]
) => {
  for (let i = 0; i < tournamentIds.length; i++) {
    const tournamentId = tournamentIds[i];
    const rosterId = selectTournamentRosterIdForDriverId(
      state,
      driverId,
      tournamentId
    );

    if (!!rosterId) {
      return selectRosterOrgDetails(state, rosterId, tournamentId);
    }
  }

  return undefined;
};

export const selectTournamentDriverName = (
  state: RootState,
  driverId: string,
  tournamentId: string
) => {
  const teams = selectTournamentTeams(state, tournamentId);
  return !!teams ? getTournamentDriverName(teams, driverId) : undefined;
};

export const selectDriverNameFromTournaments = (
  state: RootState,
  driverId: string,
  tournamentIds: string[]
) => {
  for (let i = 0; i < tournamentIds.length; i++) {
    const tournamentId = tournamentIds[i];
    const teams = selectTournamentTeams(state, tournamentId);
    const driverName = !!teams
      ? getTournamentDriverName(teams, driverId)
      : undefined;
    if (!!driverName) {
      return driverName;
    }
  }

  return undefined;
};

export const selectTournamentDriverNicknamesById = (
  state: RootState,
  ids: string[],
  tournamentId: string
) => {
  const driverNameMap = new Map<string, string | undefined>();
  ids.forEach((id) =>
    driverNameMap.set(id, selectTournamentDriverName(state, id, tournamentId))
  );

  return driverNameMap;
};

export const selectTournamentDriverDetails = (
  state: RootState,
  rosterId: string,
  tournamentId: string
) => {
  const teams = selectTournamentTeams(state, tournamentId);
  return !!teams ? getTournamentDriverDetails(teams, rosterId) : undefined;
};
